import { createContext, useContext, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { TieredMenu } from "primereact/tieredmenu";
import { MenuItem } from "primereact/menuitem";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { StatusIcon } from "./StatusIcon";
import { RecipientEditor } from "../components/RecipientEditor";
import styles from "./ReportActionMenu.module.css";
import {
  useAddComment,
  useClearanceRecipients,
  useDeleteReport,
  useUpdateReport,
  useUsers,
} from "../data";
import * as client from "../../../client";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { displayNameForUser, isAutomationAccount } from "../../../util";
import { SafeReportMetadataResponse } from "../Conversion";
import { useClientId, useFeaturesForClientId } from "../../../ClientConfig";
import { featureEnabled } from "../../../client-features";
import { confirmDialog } from "primereact/confirmdialog";
import { runFixSov, sendClearanceEmail } from "../../../api/routes";
import { apiErrorDetail } from "../../../api/errors";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../routes";
import { getAllStatuses, isClearanceStatus } from "./util";
import { useToast } from "../../../context-providers/Toast";

type ReportActionMenuProps = {
  report: SafeReportMetadataResponse;
  isUpcomingRenewal?: boolean;
  setActionMenuIsOpen?: (value: boolean) => void;
  raised?: boolean;
  onCreateBor?: () => void;
  onManageBor?: () => void;
  // Navigate to the default route after a successful delete. Used on the
  // report detail page (where the report disappears out from under us);
  // unset on the dashboard, where invalidating the list is enough.
  navigateAfterDelete?: boolean;
};

const userListWithSeparators = (
  allUsers: client.User[],
  title: string,
  filterLogic: (user: client.User) => boolean,
  onSelect: (userId: string | null) => void
) => {
  const humans = allUsers.filter((u) => !isAutomationAccount(u));
  const underwriters = humans.filter(filterLogic);
  const others = humans.filter((user) => !filterLogic(user));

  const items: MenuItem[] = [];

  // Add underwriters section
  if (underwriters.length > 0) {
    items.push({
      label: title,
      disabled: true,
      className: styles.header,
    });
  }
  items.push(
    ...underwriters.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          imageUrl={user.profile_image_url}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add separator if both sections exist
  if (underwriters.length > 0 && others.length > 0) {
    items.push({ separator: true });
  }

  // Add others section (including null option)
  if (others.length > 0) {
    items.push({
      label: "Other Users",
      disabled: true,
      className: styles.header,
    });
  }

  items.push(
    ...others.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          imageUrl={user.profile_image_url}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add null option to others section
  items.push({
    icon: (
      <PantheonAvatarWithoutMenu
        label={undefined}
        imageUrl={null}
        showName={true}
      />
    ),
    command: () => {
      onSelect(null);
    },
  });

  return items;
};

type ClearanceEmailDialogProps = {
  visible: boolean;
  onHide: () => void;
  reportId: string;
  status: client.status;
};

const ClearanceEmailDialog: React.FC<ClearanceEmailDialogProps> = ({
  visible,
  onHide,
  reportId,
  status,
}) => {
  const toast = useToast();
  const updateReport = useUpdateReport(reportId);
  const [sending, setSending] = useState(false);

  const recipientsQuery = useClearanceRecipients(reportId, visible);

  const recipients = recipientsQuery.data?.recipients ?? [];
  const lockedEmails = recipientsQuery.data?.locked_emails ?? [];
  const mutating = updateReport.isPending || recipientsQuery.isFetching;

  const persistRecipients = (nextEditable: string[]) => {
    updateReport.mutate(
      { submissionRecipients: nextEditable },
      {
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Failed to save recipients",
          });
        },
      }
    );
  };

  const send = async () => {
    setSending(true);
    try {
      await sendClearanceEmail(reportId, status);
      toast.show({
        severity: "success",
        summary: "Email Sent",
        detail: `Clearance email sent to ${recipients.length} recipient(s)`,
      });
      onHide();
    } catch {
      toast.show({
        severity: "error",
        summary: "Email Failed",
        detail: "Failed to send clearance email",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      header="Send Clearance Email"
      visible={visible}
      onHide={() => {
        if (sending) return;
        onHide();
      }}
      style={{ width: "480px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Button
            label="Cancel"
            severity="secondary"
            text
            onClick={onHide}
            disabled={sending}
          />
          <Button
            label="Send"
            onClick={send}
            loading={sending}
            disabled={mutating || recipients.length === 0}
          />
        </div>
      }
    >
      <RecipientEditor
        recipients={recipients}
        lockedEmails={lockedEmails}
        isLoading={recipientsQuery.isPending}
        mutating={mutating}
        disabled={sending}
        onPersist={persistRecipients}
      />
    </Dialog>
  );
};

// Copy for the reason prompt, keyed by the status that triggers it. Both "lost"
// and "closed" capture a free-text reason that is stored as a report comment.
const REASON_DIALOG_COPY: Partial<
  Record<client.status, { header: string; prompt: string }>
> = {
  lost: {
    header: "Reason for not taking up",
    prompt: "Why was this deal not taken up?",
  },
  closed: {
    header: "Reason for not quoting",
    prompt: "Why was this submission reviewed but not quoted?",
  },
};

const ReasonDialog: React.FC<{
  status: client.status | null;
  onHide: () => void;
  onSave: (reason: string) => void;
  saving: boolean;
}> = ({ status, onHide, onSave, saving }) => {
  const [value, setValue] = useState("");
  const copy = status ? REASON_DIALOG_COPY[status] : undefined;
  const handleHide = () => {
    setValue("");
    onHide();
  };
  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      handleHide();
      return;
    }
    onSave(trimmed);
    setValue("");
  };
  return (
    <Dialog
      header={copy?.header ?? ""}
      visible={copy !== undefined}
      onHide={handleHide}
      style={{ width: "480px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Button
            label="Skip"
            severity="secondary"
            text
            onClick={handleHide}
            disabled={saving}
          />
          <Button label="Save" onClick={handleSave} loading={saving} />
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div>{copy?.prompt}</div>
        <InputTextarea
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          placeholder="Enter a reason..."
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>
    </Dialog>
  );
};

const FixSovDialog: React.FC<{
  reportId: string;
  onHide: () => void;
}> = ({ reportId, onHide }) => {
  const toast = useToast();
  const [instructions, setInstructions] = useState("");
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setStarting(true);
    const trimmed = instructions.trim();
    try {
      await runFixSov(reportId, trimmed === "" ? null : trimmed);
      toast.show({
        severity: "success",
        summary: "SOV fix queued",
        detail: "The run posts its results to #clearance when it finishes.",
      });
      onHide();
    } catch (e) {
      toast.show({
        severity: "error",
        summary: "Failed to queue SOV fix",
        detail: apiErrorDetail(e),
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog
      header="Fix SOV"
      visible={true}
      onHide={() => {
        if (starting) return;
        onHide();
      }}
      style={{ width: "480px" }}
      footer={
        <div className={styles.buttonContainer}>
          <Button
            label="Cancel"
            severity="secondary"
            text
            onClick={onHide}
            disabled={starting}
          />
          <Button label="Run" onClick={start} loading={starting} />
        </div>
      }
    >
      <div className={styles.fixSovDialogBody}>
        <div>
          Runs an agent over the broker SOV until the parsed TIV ties to this
          report's target, re-uploading a corrected SOV if it finds a gap.
          Clearance re-runs afterwards, against the fixed report.
        </div>
        <InputTextarea
          autoFocus
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          placeholder="Optional instructions for the run, e.g. the TIV column is split across two sheets"
          className={styles.fixSovInstructions}
          disabled={starting}
        />
      </div>
    </Dialog>
  );
};

type DialogRequest = { reportId: string; status: client.status };

const ReasonDialogController: React.FC<{
  request: DialogRequest;
  onHide: () => void;
}> = ({ request, onHide }) => {
  const addComment = useAddComment(request.reportId);
  const toast = useToast();
  return (
    <ReasonDialog
      status={request.status}
      onHide={onHide}
      saving={addComment.isPending}
      onSave={async (reason) => {
        try {
          await addComment.mutateAsync(reason);
          onHide();
        } catch {
          toast.show({
            severity: "error",
            summary: "Failed to save reason",
          });
        }
      }}
    />
  );
};

type ReportDialogsContextValue = {
  openClearanceEmail: (reportId: string, status: client.status) => void;
  openReason: (reportId: string, status: client.status) => void;
  openFixSov: (reportId: string) => void;
};

const ReportDialogsContext = createContext<ReportDialogsContextValue | null>(
  null
);

const useReportDialogs = (): ReportDialogsContextValue => {
  const ctx = useContext(ReportDialogsContext);
  if (!ctx) {
    throw new Error(
      "useReportActionMenuItems must be used within a ReportDialogsProvider"
    );
  }
  return ctx;
};

export const ReportDialogsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [clearanceDialog, setClearanceDialog] = useState<DialogRequest | null>(
    null
  );
  const [reasonDialog, setReasonDialog] = useState<DialogRequest | null>(null);
  const [fixSovReportId, setFixSovReportId] = useState<string | null>(null);

  const value = useMemo<ReportDialogsContextValue>(
    () => ({
      openClearanceEmail: (reportId, status) =>
        setClearanceDialog({ reportId, status }),
      openReason: (reportId, status) => setReasonDialog({ reportId, status }),
      openFixSov: (reportId) => setFixSovReportId(reportId),
    }),
    []
  );

  return (
    <ReportDialogsContext.Provider value={value}>
      {children}
      {clearanceDialog && (
        <ClearanceEmailDialog
          visible={true}
          onHide={() => setClearanceDialog(null)}
          reportId={clearanceDialog.reportId}
          status={clearanceDialog.status}
        />
      )}
      {reasonDialog && (
        <ReasonDialogController
          request={reasonDialog}
          onHide={() => setReasonDialog(null)}
        />
      )}
      {fixSovReportId && (
        <FixSovDialog
          reportId={fixSovReportId}
          onHide={() => setFixSovReportId(null)}
        />
      )}
    </ReportDialogsContext.Provider>
  );
};

export const useReportActionMenuItems = (
  report: SafeReportMetadataResponse | null,
  isUpcomingRenewal?: boolean,
  onCreateBor?: () => void,
  onManageBor?: () => void,
  navigateAfterDelete?: boolean
): MenuItem[] => {
  const clientId = useClientId();
  const users = useUsers().data;
  const lobUsers =
    users?.all_company_users.filter((u) =>
      report ? u.lobs.includes(report.lob) : false
    ) ?? [];
  const navigate = useNavigate();
  const features = useFeaturesForClientId(clientId, navigate);
  const updateReport = useUpdateReport(report?.report_id ?? "");
  const deleteReport = useDeleteReport();
  const toast = useToast();
  const { openClearanceEmail, openReason, openFixSov } = useReportDialogs();

  const items = useMemo(() => {
    if (!report) return [];
    const baseItems: MenuItem[] = [
      {
        label: "Update status",
        icon: "pi pi-file-edit",
        items: getAllStatuses().map(
          (status): MenuItem => ({
            icon: <StatusIcon status={status} />,
            command: async () => {
              await updateReport.mutateAsync({
                status: { new_status: status },
              });
              if (isClearanceStatus(status)) {
                openClearanceEmail(report.report_id, status);
              }
              if (status === "lost" || status === "closed") {
                openReason(report.report_id, status);
              }
            },
          })
        ),
      },
      {
        label: featureEnabled(clientId, "assignee-named-underwriter")
          ? "Update underwriter"
          : "Update assignee",
        icon: "pi pi-user-edit",
        items: featureEnabled(clientId, "underwriter-handler-separation")
          ? userListWithSeparators(
              lobUsers,
              "Underwriters",
              () => true,
              (userId) =>
                updateReport.mutate({
                  assignee: { new_assignee: userId },
                })
            )
          : (lobUsers as (client.User | null)[])
              .concat([null])
              // null is the "unassign" option — keep it.
              .filter((u) => u === null || !isAutomationAccount(u))
              .map((user) => {
                const label = user ? displayNameForUser(user) : undefined;
                return {
                  icon: (
                    <PantheonAvatarWithoutMenu
                      label={label}
                      imageUrl={user?.profile_image_url}
                      showName={true}
                    />
                  ),
                  command: () => {
                    updateReport.mutate({
                      assignee: { new_assignee: user ? user.id : null },
                    });
                  },
                };
              }),
      },
      ...(featureEnabled(clientId, "handler-column")
        ? [
            {
              label: "Update specialist",
              icon: "pi pi-users",
              items: featureEnabled(clientId, "underwriter-handler-separation")
                ? userListWithSeparators(
                    lobUsers,
                    "Specialists",
                    () => true,
                    (userId) =>
                      updateReport.mutate({
                        additionalAssignees: {
                          assignees: userId ? { handler: userId } : {},
                        },
                      })
                  )
                : (lobUsers as (client.User | null)[])
                    .concat([null])
                    .map((user) => {
                      const label = user ? displayNameForUser(user) : undefined;
                      return {
                        icon: (
                          <PantheonAvatarWithoutMenu
                            label={label}
                            imageUrl={user?.profile_image_url}
                            showName={true}
                          />
                        ),
                        command: () => {
                          updateReport.mutate({
                            additionalAssignees: {
                              assignees: user ? { handler: user.id } : {},
                            },
                          });
                        },
                      };
                    }),
            },
          ]
        : []),
    ];

    if (onManageBor) {
      baseItems.push({
        label: "Manage BOR",
        icon: "pi pi-arrows-h",
        command: () => onManageBor(),
      });
    } else if (onCreateBor) {
      baseItems.push({
        label: "Create BOR letter",
        icon: "pi pi-arrows-h",
        command: () => onCreateBor(),
      });
    }

    if (
      featureEnabled(clientId, "fix-sov-run", { user: users?.current_user })
    ) {
      baseItems.push({
        label: "Fix SOV",
        icon: "pi pi-sparkles",
        command: () => openFixSov(report.report_id),
      });
    }

    if (users?.current_user.is_client_admin) {
      if (
        featureEnabled(clientId, "submission-history", {
          user: users.current_user,
        })
      ) {
        baseItems.push({
          label: "View history",
          icon: "pi pi-history",
          command: () => {
            navigate(
              ROUTES.DASHBOARD_SUBMISSION_HISTORY_DETAIL(report.report_id)
            );
          },
        });
      }
      baseItems.push({
        label: "Delete report",
        icon: "pi pi-trash",
        command: () =>
          confirmDialog({
            message: "Are you sure you want to delete this report?",
            header: "Delete Report",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
              await deleteReport.mutateAsync(report.report_id);
              if (navigateAfterDelete) {
                navigate(features.defaultRoute(users.current_user));
              }
              toast.show({
                severity: "success",
                summary: `${report.report_metadata.company_info.company_name} deleted`,
              });
            },
          }),
      });
    }

    return baseItems;
  }, [
    report,
    users,
    updateReport,
    isUpcomingRenewal,
    navigateAfterDelete,
    openClearanceEmail,
    openReason,
    openFixSov,
  ]);

  return items;
};

export const ReportActionMenu: React.FC<ReportActionMenuProps> = ({
  report,
  isUpcomingRenewal,
  setActionMenuIsOpen,
  raised = false,
  onCreateBor,
  onManageBor,
  navigateAfterDelete,
}) => {
  const menu = useRef<TieredMenu>(null);
  const items = useReportActionMenuItems(
    report,
    isUpcomingRenewal,
    onCreateBor,
    onManageBor,
    navigateAfterDelete
  );

  return (
    // Stop click propagation: PrimeReact portals its menu / dialogs to
    // document.body but React events still bubble through the React tree,
    // which would otherwise trigger the dashboard row's row-click navigation.
    <div onClick={(e) => e.stopPropagation()}>
      <TieredMenu
        model={items}
        popup
        ref={menu}
        style={{ width: "232px" }}
        pt={{ submenu: { style: { maxHeight: "600px", overflowY: "auto" } } }}
        breakpoint="767px"
        onShow={() => setActionMenuIsOpen && setActionMenuIsOpen(true)}
        onHide={() => setActionMenuIsOpen && setActionMenuIsOpen(false)}
      />
      <Button
        text
        raised={raised}
        severity="secondary"
        icon="pi pi-ellipsis-v"
        onClick={(e) => {
          menu.current?.toggle(e);
        }}
      />
    </div>
  );
};
